import type { FieldState } from "@/api/hooks";

/**
 * Grouping the Clinical Summary's effective projection into something a
 * clinician can read (#373).
 *
 * WHY THIS EXISTS. `GET /v1/projects/{id}/sections/summary` serves the whole
 * `summary_agent` area of the pack as one flat `fields` array — 171 entries on
 * the ratified base pack. `SectionFieldList` renders that array as a
 * two-column grid of dotted addresses, which is the right presentation for a
 * six-field section and is unreadable for this one: the summary's fixed report
 * text, its display labels, its per-section generation instructions and the
 * model's tuning constants all arrive interleaved in one alphabetical run.
 *
 * WHAT IT DOES, AND WHAT IT REFUSES TO DO. It reads the SERVER'S OWN addresses
 * and nothing else. `FieldState.path` is documented as "stable dotted address
 * of the field, spelled exactly as a write would address it", and the pack's
 * own layout is what puts `en` under a language and `fixed_values` under an
 * area — so grouping on those segments re-presents a structure the service
 * supplied rather than one this module decided on. No value is parsed, no
 * clinical meaning is derived, nothing is inferred from content, and no field
 * is dropped: a path that does not match the expected shape still appears, in
 * its own group, rather than vanishing because it was unexpected.
 *
 * The `label` on a group is presentation, exactly like `fieldLabel` in
 * `lib/fieldState.ts`, and the raw segments travel with it so nothing on
 * screen depends on the derived text.
 */

export type SummaryFieldGroup = {
  /** Stable key: the address prefix the group's fields share. */
  key: string;
  /** Clinician-facing heading derived from that prefix. */
  label: string;
  /**
   * What the group is, in one sentence, or `undefined` for a shape this module
   * did not anticipate — where saying nothing is more honest than guessing.
   */
  description: string | undefined;
  /** The language segment, when the address carries one. */
  language: string | undefined;
  fields: readonly FieldState[];
};

/**
 * The pack areas the summary projection is known to serve, in reading order.
 *
 * Keyed on the SECOND segment of the address for the language-neutral areas and
 * on the segment after the language for the per-language ones. An area absent
 * from this table is not an error — it groups under its own raw segment with no
 * description, which is what keeps a pack that grows a new area visible rather
 * than silently flattened.
 */
const AREA_LABELS: Record<string, { label: string; description: string }> = {
  fixed_values: {
    label: "Fixed report text",
    description:
      "Wording that appears in every summary this project produces, exactly as written here.",
  },
  labels: {
    label: "Display labels",
    description:
      "The names the summary gives to tracks, diagnoses and assessments when it reports them.",
  },
  per_key: {
    label: "Per-section instructions",
    description:
      "What the summary is instructed to produce for each of its sections, and the worked examples it is given.",
  },
  generation: {
    label: "Generation settings",
    description:
      "Model settings the summary is produced under. Not clinical content, and shown because they are part of this project's effective configuration.",
  },
};

/** `summary.<language>.<area>.…` — the two-letter language codes the pack uses. */
const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  he: "Hebrew",
};

function languageLabel(code: string): string {
  return LANGUAGE_LABELS[code] ?? code;
}

/**
 * Splits `path` into the prefix its group is keyed on and the segments that
 * describe it.
 *
 * Two shapes are recognised, both taken from the projection's own addresses:
 *
 *   `summary.<language>.<area>.<rest…>`  →  a per-language area
 *   `summary.<area>.<rest…>`             →  a language-neutral area
 *
 * A language is recognised by being a member of `LANGUAGE_LABELS`, never by
 * position or length: `summary.config.generation.temperature` has a
 * two-segment head like a language address does, and reading `config` as a
 * language would file the model's tuning constants under a language that does
 * not exist.
 */
function classify(path: string): {
  key: string;
  area: string;
  language: string | undefined;
} {
  const segments = path.split(".");
  // Everything the projection serves for this section is addressed under the
  // section slug. Anything else keeps its own first segment as the group.
  const head = segments[0] === "summary" ? segments.slice(1) : segments;
  const first = head[0];
  if (first === undefined) {
    return { key: path, area: path, language: undefined };
  }
  if (first in LANGUAGE_LABELS && head.length > 1) {
    const area = head[1] as string;
    return { key: `${first}.${area}`, area, language: first };
  }
  const area = head.length > 1 ? (head[1] as string) : first;
  return { key: head.slice(0, 2).join("."), area, language: undefined };
}

/**
 * Groups the section's fields, preserving the server's order within each group
 * and ordering the groups by first appearance.
 *
 * Order is the server's, not a preference of this module's: the projection
 * returns its fields sorted by address, and re-sorting them here would put a
 * presentation decision in front of the composition the engine performed.
 */
export function groupSummaryFields(
  fields: readonly FieldState[],
): readonly SummaryFieldGroup[] {
  const byKey = new Map<string, { group: SummaryFieldGroup; fields: FieldState[] }>();
  const order: string[] = [];

  for (const field of fields) {
    const { key, area, language } = classify(field.path);
    let entry = byKey.get(key);
    if (!entry) {
      const known = AREA_LABELS[area];
      const areaLabel = known?.label ?? area;
      entry = {
        group: {
          key,
          label: language ? `${areaLabel} — ${languageLabel(language)}` : areaLabel,
          description: known?.description,
          language,
          fields: [],
        },
        fields: [],
      };
      byKey.set(key, entry);
      order.push(key);
    }
    entry.fields.push(field);
  }

  return order.map((key) => {
    const entry = byKey.get(key) as { group: SummaryFieldGroup; fields: FieldState[] };
    return { ...entry.group, fields: entry.fields };
  });
}

/**
 * How many of the section's values this project supplied itself.
 *
 * Counted off the contract's `provenance` enum, which is server-derived from
 * each layer's explicit supply — never guessed from a value differing from
 * base. `base` is the pinned base content; `overridden`, `adopted` and `added`
 * are this project's overlay (its draft, or an adopted library version);
 * `locked` is content the engine refuses to let an overlay weaken.
 */
export function summaryProvenanceCounts(fields: readonly FieldState[]): {
  total: number;
  fromBase: number;
  fromProject: number;
  locked: number;
} {
  let fromBase = 0;
  let fromProject = 0;
  let locked = 0;
  for (const field of fields) {
    if (field.lock.state === "locked" || field.provenance === "locked") {
      locked += 1;
      continue;
    }
    if (
      field.provenance === "overridden" ||
      field.provenance === "adopted" ||
      field.provenance === "added"
    ) {
      // Supplied by this project — by its draft (`overridden`/`added` with no
      // origin) or by an adopted library version (`adopted`, or `added` with an
      // adoption origin). An adoption that REPLACES a base value is the
      // project's supply exactly as an addition is: the base pack shipped
      // 172 values and this project supplies one of them.
      fromProject += 1;
      continue;
    }
    fromBase += 1;
  }
  return { total: fields.length, fromBase, fromProject, locked };
}
